import { useEffect, useRef } from "react";

/**
 * Lightweight canvas particle / data-node field used as a subtle background
 * effect. Designed to be unobtrusive — small count, slow movement, low alpha.
 */
export default function ParticleField({
    color = "rgba(255, 64, 64, 0.55)",
    lineColor = "rgba(255, 255, 255, 0.06)",
    density = 28,
    className = "",
}) {
    const canvasRef = useRef(null);
    const rafRef = useRef(0);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");

        let width = canvas.offsetWidth;
        let height = canvas.offsetHeight;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);

        const setSize = () => {
            width = canvas.offsetWidth;
            height = canvas.offsetHeight;
            canvas.width = width * dpr;
            canvas.height = height * dpr;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        };
        setSize();

        const count = Math.min(density, Math.floor((width * height) / 25000));
        const nodes = Array.from({ length: count }).map(() => ({
            x: Math.random() * width,
            y: Math.random() * height,
            vx: (Math.random() - 0.5) * 0.25,
            vy: (Math.random() - 0.5) * 0.25,
            r: Math.random() * 1.6 + 0.6,
        }));

        const draw = () => {
            ctx.clearRect(0, 0, width, height);

            // Lines between near-neighbours
            for (let i = 0; i < nodes.length; i++) {
                for (let j = i + 1; j < nodes.length; j++) {
                    const a = nodes[i];
                    const b = nodes[j];
                    const dx = a.x - b.x;
                    const dy = a.y - b.y;
                    const dist = Math.sqrt(dx * dx + dy * dy);
                    if (dist < 140) {
                        ctx.strokeStyle = lineColor;
                        ctx.lineWidth = 1;
                        ctx.globalAlpha = 1 - dist / 140;
                        ctx.beginPath();
                        ctx.moveTo(a.x, a.y);
                        ctx.lineTo(b.x, b.y);
                        ctx.stroke();
                    }
                }
            }
            ctx.globalAlpha = 1;

            for (const n of nodes) {
                n.x += n.vx;
                n.y += n.vy;
                if (n.x < 0 || n.x > width) n.vx *= -1;
                if (n.y < 0 || n.y > height) n.vy *= -1;
                ctx.fillStyle = color;
                ctx.beginPath();
                ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
                ctx.fill();
            }

            rafRef.current = requestAnimationFrame(draw);
        };
        rafRef.current = requestAnimationFrame(draw);

        const onResize = () => setSize();
        window.addEventListener("resize", onResize);
        return () => {
            cancelAnimationFrame(rafRef.current);
            window.removeEventListener("resize", onResize);
        };
    }, [color, lineColor, density]);

    return (
        <canvas
            ref={canvasRef}
            className={`absolute inset-0 w-full h-full pointer-events-none ${className}`}
            aria-hidden="true"
        />
    );
}
